<template>
  <UModal v-model:open="isOpen" :ui="{ width: 'w-full sm:max-w-3xl' }">
    <template #content>
      <div class="p-6">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-bold text-gray-900 dark:text-white">
            Rechercher
          </h2>
          <UButton
            color="gray"
            variant="ghost"
            icon="i-heroicons-x-mark-20-solid"
            @click="isOpen = false"
          />
        </div>
        <div class="relative mb-6">
          <UInput
            v-model="searchQuery"
            icon="i-heroicons-magnifying-glass-20-solid"
            size="xl"
            :ui="{
              rounded: 'rounded-lg',
              icon: { trailing: { pointer: '' } },
              base: 'w-full',
            }"
            placeholder="Rechercher un repas ou un menu..."
            autofocus
          >
            <template #trailing>
              <UButton
                v-if="searchQuery !== ''"
                color="gray"
                variant="link"
                icon="i-heroicons-x-mark-20-solid"
                :padded="false"
                @click="searchQuery = ''"
              />
            </template>
          </UInput>
        </div>

        <div class="space-y-6 max-h-[60vh] overflow-y-auto">
          <!-- Results Section -->
          <div v-if="filteredResults.length > 0">
            <div v-if="filteredMeals.length > 0" class="mb-6">
              <h3
                class="font-semibold mb-3 text-gray-400 uppercase text-xs tracking-wider"
              >
                Repas
              </h3>
              <div class="grid gap-3">
                <div
                  v-for="meal in filteredMeals"
                  :key="'meal-' + meal.id"
                  class="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  @click="handleMealSelect(meal)"
                >
                  <NuxtImg
                    :src="getImageUrl(meal.image)"
                    class="w-16 h-16 rounded-md object-cover"
                  />
                  <div class="flex-1">
                    <h4 class="font-medium text-gray-900 dark:text-white">
                      {{ meal.name }}
                    </h4>
                    <p class="text-sm text-gray-500 line-clamp-1">
                      {{ meal.ingredients }}
                    </p>
                  </div>
                  <span class="font-bold text-(--main-color)"
                    >{{ meal.price }} €</span
                  >
                </div>
              </div>
            </div>

            <div v-if="filteredMenus.length > 0">
              <h3
                class="font-semibold mb-3 text-gray-400 uppercase text-xs tracking-wider"
              >
                Menus
              </h3>
              <div class="grid gap-3">
                <div
                  v-for="menu in filteredMenus"
                  :key="'menu-' + menu.id"
                  class="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                  @click="handleMenuSelect(menu)"
                >
                  <NuxtImg
                    :src="getImageUrl(menu.image)"
                    class="w-16 h-16 rounded-md object-cover"
                  />
                  <div class="flex-1">
                    <h4 class="font-medium text-gray-900 dark:text-white">
                      {{ menu.name }}
                    </h4>
                  </div>
                  <span class="font-bold text-(--main-color)"
                    >{{ menu.price }} €</span
                  >
                </div>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div
            v-else-if="searchQuery && !pending"
            class="text-center py-10 text-gray-500"
          >
            <UIcon name="i-heroicons-face-frown" class="text-4xl mb-2" />
            <p>Aucun résultat trouvé pour "{{ searchQuery }}"</p>
          </div>

          <!-- Initial/Loading State -->
          <div v-else-if="pending" class="text-center py-10">
            <UIcon
              name="i-heroicons-arrow-path"
              class="animate-spin text-4xl text-(--main-color)"
            />
          </div>
        </div>
      </div>
    </template>
  </UModal>

  <ClientOnly>
    <MealCustomizeDrawer
      v-if="selectedMeal"
      v-model="isDrawerOpen"
      :meal="selectedMeal"
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import { ref, computed } from "#imports";
import type { Repas } from "~/models/repas";
import type { Menu } from "~/models/menu";

const props = withDefaults(
  defineProps<{
    open?: boolean;
  }>(),
  {
    open: false,
  },
);

const emit = defineEmits(["update:open"]);

const { $api } = useNuxtApp();
const router = useRouter();

const searchQuery = ref("");
const isDrawerOpen = ref(false);
const selectedMeal = ref<Repas | null>(null);

const isOpen = computed({
  get: () => props.open,
  set: (value) => emit("update:open", value),
});

// Fetch Data
const { data: allMeals, pending: pendingMeals } = await useAsyncData<Repas[]>(
  "search-meals",
  () => $api.get("/repas"),
);

const { data: allMenus, pending: pendingMenus } = await useAsyncData<Menu[]>(
  "search-menus",
  () => $api.get("/menus"),
);

const pending = computed(() => pendingMeals.value || pendingMenus.value);

// Computed Results
const filteredMeals = computed(() => {
  if (!allMeals.value) return [];
  if (!searchQuery.value) return allMeals.value.slice(0, 5); // Show first 5 by default
  const query = searchQuery.value.toLowerCase();
  return allMeals.value.filter((m) => m.name.toLowerCase().includes(query));
});

const filteredMenus = computed(() => {
  if (!allMenus.value) return [];
  if (!searchQuery.value) return allMenus.value.slice(0, 5); // Show first 5 by default
  const query = searchQuery.value.toLowerCase();
  return allMenus.value.filter((m) => m.name.toLowerCase().includes(query));
});

const filteredResults = computed(() => [
  ...filteredMeals.value,
  ...filteredMenus.value,
]);

// Actions
const handleMealSelect = (meal: Repas) => {
  selectedMeal.value = meal;
  isDrawerOpen.value = true;
  // Don't close modal immediately, maybe allow closing it manually or after selection?
  // Let's keep modal open efficiently or close it?
  // Usually search modal closes on selection.
  // But we open a drawer, which overlays.
  // If we close modal, the drawer shows up.
  // Let's close modal.
  isOpen.value = false;
};

const handleMenuSelect = (_menu: Menu) => {
  router.push(`/menus`); // Or specific menu page if available?
  // The user asked to show menu list in hero originally.
  // Menus page seems to list menus.
  // Let's redirect to /menus for now or handle specific menu if we have a route.
  // There is no /menus/:id page in the router list I saw earlier (only /repas/:id in controller).
  // Menus controller has getById.
  // But checking nav-bar, it just points to /menus.
  // We might need a menu detail or just go to /menus.
  // Let's going with /menus for now.
  isOpen.value = false;
};

// Reset search on open?
watch(isOpen, (newVal) => {
  if (newVal) {
    // maybe focus input
  }
});
</script>
